import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getPublicFileBlob } from '@/api/query/file';
import { studioVoicePreviewQueryKey } from './studioVoicePreviewQuery';

const createWarmPreviewAudio = (blob) => {
	const blobUrl = URL.createObjectURL(blob);
	const audio = new Audio(blobUrl);
	audio.preload = 'auto';
	audio.load();

	return { blobUrl, audio };
};

export const fetchStudioVoicePreview = async (fileId) => {
	const blob = await getPublicFileBlob(fileId);

	return createWarmPreviewAudio(blob);
};

export const useStudioVoicePreviewPreload = (fileIds = []) => {
	const uniqueFileIds = useMemo(
		() => [...new Set(fileIds.filter(Boolean))],
		[fileIds],
	);

	useQueries({
		queries: uniqueFileIds.map((fileId) => ({
			queryKey: studioVoicePreviewQueryKey(fileId),
			queryFn: () => fetchStudioVoicePreview(fileId),
			staleTime: Infinity,
			gcTime: 30 * 60 * 1000,
			retry: 1,
		})),
	});
};

export const useStudioVoicePreview = () => {
	const queryClient = useQueryClient();
	const activePreviewAudioRef = useRef(null);

	const stop = useCallback(() => {
		const audio = activePreviewAudioRef.current;

		if (!audio) {
			return;
		}

		audio.pause();
		audio.currentTime = 0;
		activePreviewAudioRef.current = null;
	}, []);

	const play = useCallback(
		(fileId) => {
			if (!fileId) {
				return;
			}

			const cached = queryClient.getQueryData(studioVoicePreviewQueryKey(fileId));

			if (!cached?.audio) {
				return;
			}

			if (
				activePreviewAudioRef.current &&
				activePreviewAudioRef.current !== cached.audio
			) {
				activePreviewAudioRef.current.pause();
				activePreviewAudioRef.current.currentTime = 0;
			}

			const { audio } = cached;
			audio.currentTime = 0;
			audio.onended = stop;
			audio.onerror = stop;
			activePreviewAudioRef.current = audio;

			audio.play().catch(stop);
		},
		[queryClient, stop],
	);

	const prefetch = useCallback(
		(fileId) => {
			if (!fileId) {
				return Promise.resolve();
			}

			return queryClient.prefetchQuery({
				queryKey: studioVoicePreviewQueryKey(fileId),
				queryFn: () => fetchStudioVoicePreview(fileId),
				staleTime: Infinity,
			});
		},
		[queryClient],
	);

	useEffect(() => () => stop(), [stop]);

	return useMemo(
		() => ({
			play,
			stop,
			prefetch,
		}),
		[play, stop, prefetch],
	);
};
